import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useOutletContext } from 'react-router-dom';
import { HeartPulse, Compass, ChevronRight } from 'lucide-react';
import Icon, { type IconName } from '../../../components/AppIcon';
import KPICard, { type KPIMetric } from './KpiCard';
import { getENPSOverview } from '../../../api/PreformanceManagement/ENPSAnalytics';
import type { ENPSOverviewView } from '../../../models/preformanceManagement';
import type { FilteredNavigationState } from '../../../hooks/useFilteredNavigation';

/**
 * CO-815 — the company-overview part of Home, for the company-scope readers (Chief and HR).
 *
 * The owner's ask: a Chief "should have visibility over the engagement modules and see dashboards that
 * give an overview over how the employees are doing". The KPI groups above already answer attendance,
 * headcount and projects at company scope. This adds the two things they do not:
 *
 *   1. ENGAGEMENT: the company eNPS and survey participation, in the same KPICard vocabulary as the
 *      groups above (no new visual language). Shown only when the reader can open eNPS analytics, and
 *      suppressed below the anonymity threshold (`insufficientData`) instead of showing a thin number.
 *   2. GO DEEPER: one list of the company dashboards this reader can actually open. It is derived from
 *      the reader's FILTERED navigation (role gates + company module/page config already applied), so a
 *      link here can never land on a 403 or a switched-off page.
 *
 * Deliberately not here: payroll or any salary figure (Chief is not HR), and anything that composes a
 * sentence out of metrics. Every figure has a place to go for its detail.
 */

type OverviewLink = { path: string; icon: IconName; titleKey: string; hintKey: string };

/** Company-wide read surfaces, in the order a reader scans them. Only those in the reader's nav render. */
const OVERVIEW_LINKS: OverviewLink[] = [
  { path: '/employee-impact/engagement-dashboard', icon: 'HeartPulse', titleKey: 'companyOverview.links.engagement.title', hintKey: 'companyOverview.links.engagement.hint' },
  { path: '/hr-enps-analytics', icon: 'BarChart3', titleKey: 'companyOverview.links.enps.title', hintKey: 'companyOverview.links.enps.hint' },
  { path: '/absence-analytics', icon: 'CalendarX', titleKey: 'companyOverview.links.absence.title', hintKey: 'companyOverview.links.absence.hint' },
  { path: '/attendance/reports', icon: 'PieChart', titleKey: 'companyOverview.links.attendanceReports.title', hintKey: 'companyOverview.links.attendanceReports.hint' },
  { path: '/team-calendar', icon: 'CalendarDays', titleKey: 'companyOverview.links.teamCalendar.title', hintKey: 'companyOverview.links.teamCalendar.hint' },
  { path: '/core-hr/org-structure', icon: 'Network', titleKey: 'companyOverview.links.orgStructure.title', hintKey: 'companyOverview.links.orgStructure.hint' },
];

const ENPS_PATH = '/hr-enps-analytics';

const SectionHeading: React.FC<{ icon: React.ElementType; label: string; action?: React.ReactNode; testId: string }> = ({
  icon: HeadingIcon,
  label,
  action,
  testId,
}) => (
  <div className="flex items-center gap-2 mb-3 ps-1" data-testid={testId}>
    <HeadingIcon className="w-3.5 h-3.5 text-muted-foreground/70" strokeWidth={2.25} />
    <h3 className="text-[11px] font-semibold text-muted-foreground uppercase tracking-[0.08em]">{label}</h3>
    <div className="flex-1 h-px bg-border/60" />
    {action}
  </div>
);

const CompanyOverviewPanel: React.FC = () => {
  const { t } = useTranslation();
  // Undefined when the dashboard is rendered outside the layout outlet; then nothing is linkable.
  const navigation = useOutletContext<FilteredNavigationState | undefined>();

  const visiblePaths = useMemo(() => {
    const paths = new Set<string>();
    for (const module of navigation?.modules ?? []) {
      for (const page of module.pages) if (!page.comingSoon) paths.add(page.path);
      for (const sub of module.subModules ?? []) {
        for (const page of sub.pages) if (!page.comingSoon) paths.add(page.path);
      }
    }
    return paths;
  }, [navigation?.modules]);

  const links = useMemo(() => OVERVIEW_LINKS.filter((l) => visiblePaths.has(l.path)), [visiblePaths]);
  const canReadEnps = visiblePaths.has(ENPS_PATH);

  const [enps, setEnps] = useState<ENPSOverviewView | null>(null);
  const [enpsState, setEnpsState] = useState<'idle' | 'loading' | 'ready' | 'failed'>('idle');

  useEffect(() => {
    if (!canReadEnps) return;
    let cancelled = false;
    setEnpsState('loading');
    (async () => {
      try {
        // GET ENPSAnalytics/Overview is CURRENT-MONTH only (it ignores dateRange; the change is vs last
        // month), so the tile copy says "this month" (CO-815 review). Trends is the endpoint that honours dateRange.
        const result = await getENPSOverview({ dateRange: 'last12months', department: 'all', location: 'all' });
        if (cancelled) return;
        if (result?.errors?.length || !result?.payload) {
          setEnpsState('failed');
          return;
        }
        setEnps(result.payload);
        setEnpsState('ready');
      } catch {
        if (!cancelled) setEnpsState('failed');
      }
    })();
    return () => { cancelled = true; };
  }, [canReadEnps]);

  const engagementKPIs = useMemo<KPIMetric[]>(() => {
    if (!enps || enps.insufficientData) return [];
    const signed = (n: number) => `${n > 0 ? '+' : ''}${Math.round(n)}`;
    // A prior period below the anonymity floor has no score to compare against: the server then omits
    // `change` (2026-09-28, S4). Show no delta rather than "+32".
    const hasPrevious = enps.change != null;
    return [
      {
        id: 'engagement-enps',
        title: t('companyOverview.engagement.enps'),
        value: signed(enps.currentScore),
        ...(hasPrevious
          ? { change: signed(enps.change!), trend: enps.change! >= 0 ? ('up' as const) : ('down' as const) }
          : {}),
        icon: 'HeartPulse',
        color: 'purple',
        category: 'engagement',
        description: t('companyOverview.engagement.enpsTooltip'),
      },
      {
        id: 'engagement-participation',
        title: t('companyOverview.engagement.participation'),
        value: `${Math.round(enps.participationRate)}%`,
        // Company-wide read: totalResponses is only null on a withheld FILTERED slice, never here.
        change: t('companyOverview.engagement.responses', { count: enps.totalResponses ?? 0, eligible: enps.eligibleEmployees }),
        isNeutralChange: true,
        icon: 'Users',
        color: 'blue',
        category: 'engagement',
        description: t('companyOverview.engagement.participationTooltip'),
      },
    ];
  }, [enps, t]);

  if (!canReadEnps && links.length === 0) return null;

  return (
    <div className="space-y-6 mt-7" data-testid="company-overview-panel">
      {canReadEnps && (
        <div data-testid="company-overview-engagement">
          <SectionHeading
            icon={HeartPulse}
            label={t('companyOverview.engagement.title')}
            testId="company-overview-engagement-heading"
            action={
              <Link
                to={ENPS_PATH}
                className="text-xs font-medium text-primary hover:underline underline-offset-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm"
                data-testid="company-overview-enps-link"
              >
                {t('companyOverview.engagement.open')}
              </Link>
            }
          />
          {enpsState === 'loading' && (
            <div className="h-20 rounded-lg bg-muted animate-pulse" aria-hidden="true" />
          )}
          {enpsState === 'failed' && (
            <p className="text-sm text-muted-foreground ps-1" data-testid="company-overview-enps-failed">
              {t('companyOverview.engagement.loadFailed')}
            </p>
          )}
          {enpsState === 'ready' && enps?.insufficientData && (
            <p className="text-sm text-muted-foreground ps-1" data-testid="company-overview-enps-insufficient">
              {t('companyOverview.engagement.insufficient')}
            </p>
          )}
          {enpsState === 'ready' && engagementKPIs.length > 0 && (
            <div className="grid gap-4 grid-cols-1 md:grid-cols-2">
              {engagementKPIs.map((metric) => (
                <KPICard key={metric.id} metric={metric} isPrimary={false} />
              ))}
            </div>
          )}
        </div>
      )}

      {links.length > 0 && (
        <div data-testid="company-overview-links">
          <SectionHeading icon={Compass} label={t('companyOverview.links.title')} testId="company-overview-links-heading" />
          <ul className="divide-y divide-border rounded-lg border border-border bg-card">
            {links.map((link) => (
              <li key={link.path}>
                <Link
                  to={link.path}
                  className="group flex items-center gap-3 px-4 py-3 hover:bg-muted/50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
                  data-testid={`company-overview-link-${link.path.replace(/\//g, '-').replace(/^-/, '')}`}
                >
                  <Icon name={link.icon} size={18} className="shrink-0 text-muted-foreground group-hover:text-primary" />
                  <span className="flex-1 min-w-0">
                    <span className="block text-sm font-medium text-foreground">{t(link.titleKey)}</span>
                    <span className="block text-xs text-muted-foreground truncate">{t(link.hintKey)}</span>
                  </span>
                  <ChevronRight className="w-4 h-4 shrink-0 text-muted-foreground rtl:rotate-180" aria-hidden="true" />
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default CompanyOverviewPanel;
